import {
  Area,
  AreaChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";

import { useTheme } from "../../hooks/use-theme";
import { Footer } from "../../layouts/footer";
import {
  CreditCard,
  DollarSign,
  Package,
  PencilLine,
  Star,
  Trash,
  TrendingDown,
  TrendingUp,
  Users,
} from "lucide-react";
import { getStats } from "../../api/dashboardApi";
import { useEffect, useState } from "react";
import MonthlyIncomeChart from "./IncomeGaph";
import LatestOrders from "./LatesOrders";
import { Link } from "react-router-dom";

// Skeleton component just for stats cards
const StatsCardsSkeleton = () => {
  return (
    <div className="grid grid-cols-1 gap-6 md:grid-cols-2 lg:grid-cols-4 animate-pulse">
      {[...Array(4)].map((_, index) => (
        <div key={index} className="rounded-xl bg-_white p-6 shadow-sm">
          <div className="flex items-center justify-between">
            <div>
              <div className="h-4 w-24 bg-_gray_lite rounded-md"></div>
              <div className="mt-4 h-8 w-16 bg-_gray_lite rounded-md"></div>
              <div className="mt-2 h-4 w-32 bg-_gray_lite rounded-md"></div>
            </div>
            <div className="rounded-full bg-_gray_lite h-12 w-12"></div>
          </div>
        </div>
      ))}
    </div>
  );
};

// Full dashboard skeleton for initial loading
const DashboardSkeleton = () => {
  return (
    <div className="flex flex-col gap-y-6 px-1 py-4 md:px-4 animate-pulse">
      {/* Header Skeleton */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between">
        <div className="h-8 w-64 bg-_gray_lite rounded-md"></div>
        <div className="mt-2 flex items-center gap-2 md:mt-0">
          <div className="h-10 w-32 bg-_gray_lite rounded-md"></div>
          <div className="h-10 w-24 bg-_gray_lite rounded-md"></div>
        </div>
      </div>

      {/* Stats Cards Skeleton */}
      <StatsCardsSkeleton />

      {/* Charts and Tables Skeleton */}
      <div className="grid grid-cols-1 gap-6 lg:grid-cols-7">
        <div className="rounded-xl bg-_white p-6 shadow-sm lg:col-span-4">
          <div className="mb-4 flex items-center justify-between">
            <div className="h-6 w-48 bg-_gray_lite rounded-md"></div>
            <div className="flex items-center space-x-4">
              <div className="h-4 w-20 bg-_gray_lite rounded-md"></div>
              <div className="h-4 w-20 bg-_gray_lite rounded-md"></div>
            </div>
          </div>
          <div className="h-80 bg-_gray_lite/50 rounded-lg"></div>
        </div>

        <div className="rounded-xl bg-_white p-6 shadow-sm lg:col-span-3">
          <div className="mb-4 flex items-center justify-between">
            <div className="h-6 w-36 bg-_gray_lite rounded-md"></div>
            <div className="h-4 w-20 bg-_gray_lite rounded-md"></div>
          </div>
          <div className="h-80">
            {[...Array(5)].map((_, index) => (
              <div
                key={index}
                className="flex items-center justify-between py-3 border-b border-_gray_lite/70"
              >
                <div className="flex items-center">
                  <div className="h-10 w-10 rounded-full bg-_gray_lite"></div>
                  <div className="ml-3 flex flex-col">
                    <div className="h-4 w-28 bg-_gray_lite rounded-md"></div>
                    <div className="mt-2 h-3 w-20 bg-_gray_lite rounded-md"></div>
                  </div>
                </div>
                <div className="h-6 w-16 bg-_gray_lite rounded-md"></div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Quick Actions Skeleton */}
      <div className="rounded-xl bg-_white p-6 shadow-sm">
        <div className="mb-4 flex items-center justify-between">
          <div className="h-6 w-36 bg-_gray_lite rounded-md"></div>
        </div>
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4">
          {[...Array(4)].map((_, index) => (
            <div key={index} className="h-14 rounded-lg bg-_gray_lite"></div>
          ))}
        </div>
      </div>
    </div>
  );
};

const DashboardPage = () => {
  const { theme } = useTheme();

  const [stats, setStats] = useState(null);
  const [initialLoading, setInitialLoading] = useState(true);
  const [statsLoading, setStatsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [timePeriod, setTimePeriod] = useState("week");

  // Initial data loading
  useEffect(() => {
    const fetchInitialData = async () => {
      try {
        const data = await getStats(timePeriod);
        setStats(data);
      } catch (err) {
        setError("Error fetching stats");
      } finally {
        setInitialLoading(false);
      }
    };

    fetchInitialData();
  }, []);

  // Handle time period changes
  useEffect(() => {
    // Skip the first render since it's handled by initialLoading
    if (initialLoading) return;

    const fetchStats = async () => {
      setStatsLoading(true);
      try {
        const data = await getStats(timePeriod);
        setStats(data);
      } catch (err) {
        setError("Error fetching stats");
      } finally {
        setStatsLoading(false);
      }
    };

    fetchStats();
  }, [timePeriod, initialLoading]);

  // Initial loading state
  if (initialLoading) {
    return <DashboardSkeleton />;
  }

  // Error state
  if (error) {
    return (
      <div className="flex h-64 w-full items-center justify-center">
        <div className="rounded-lg bg-_red/20 text-_red p-4">
          <p>{error}</p>
          <button
            className="mt-2 rounded px-4 py-2 bg-_red text-_white hover:opacity-90"
            onClick={() => window.location.reload()}
          >
            Retry
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-y-6 px-1 py-4 md:px-4">
      <div className="flex flex-col md:flex-row md:items-center md:justify-between">
        <h1 className="text-2xl font-bold text-_dark md:text-3xl">
          Dashboard Overview
        </h1>
        <div className="mt-2 flex items-center gap-2 md:mt-0">
          <select
            className="rounded-md border border-_gray_lite text-_dark bg-_white px-3 py-2 text-sm focus:outline-none"
            onChange={(e) => setTimePeriod(e.target.value)}
            value={timePeriod}
            disabled={statsLoading}
          >
            <option value={"week"}>This week</option>
            <option value={"month"}>This month</option>
            <option value={"last_month"}>Last Month</option>
            <option value={"three_months"}>Last 3 Months</option>
          </select>
        </div>
      </div>

      {/* Stats Cards - Show skeleton when loading on time period change */}
      {statsLoading ? (
        <StatsCardsSkeleton />
      ) : (
        <div className="grid grid-cols-1 gap-6 md:grid-cols-2 lg:grid-cols-4">
          <div className="rounded-xl bg-_white p-6 shadow-sm transition-all hover:shadow-md">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-_gray">Total Products</p>
                <p className="mt-2 text-3xl font-bold text-_dark">
                  {stats?.productStats || 0}
                </p>
              </div>
              <div className="rounded-full bg-_blue/[.15] text-_blue p-3">
                <Package size={24} />
              </div>
            </div>
          </div>

          <div className="rounded-xl bg-_white p-6 shadow-sm transition-all hover:shadow-md">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-_gray">
                  Total Customers
                </p>
                <p className="mt-2 text-3xl font-bold text-_dark">
                  {stats?.userStats?.currentCount || 0}
                </p>
                {stats?.userStats?.percentageChange !== undefined && (
                  <p
                    className={`mt-2 flex items-center text-sm font-medium ${
                      stats?.userStats?.percentageChange >= 0
                        ? "text-_blue"
                        : "text-_red"
                    }`}
                  >
                    {stats?.userStats?.percentageChange >= 0 ? (
                      <TrendingUp size={16} className="mr-1" />
                    ) : (
                      <TrendingDown size={16} className="mr-1" />
                    )}
                    {Math.abs(stats?.userStats?.percentageChange).toFixed(2)}%{" "}
                    {stats?.userStats?.percentageChange >= 0
                      ? "increase"
                      : "decrease"}
                  </p>
                )}
              </div>
              <div className="rounded-full bg-_dark_lite/20 text-_dark_lite p-3">
                <Users size={24} />
              </div>
            </div>
          </div>

          <div className="rounded-xl bg-_white p-6 shadow-sm transition-all hover:shadow-md">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-_gray">Total Revenue</p>
                <p className="mt-2 text-3xl font-bold text-_dark">
                  LKR {stats?.incomeStats?.currentIncome?.toLocaleString() || 0}
                </p>
                {stats?.incomeStats?.percentageChange !== undefined && (
                  <p
                    className={`mt-2 flex items-center text-sm font-medium ${
                      stats?.incomeStats?.percentageChange >= 0
                        ? "text-_blue"
                        : "text-_red"
                    }`}
                  >
                    {stats?.incomeStats?.percentageChange >= 0 ? (
                      <TrendingUp size={16} className="mr-1" />
                    ) : (
                      <TrendingDown size={16} className="mr-1" />
                    )}
                    {Math.abs(stats?.incomeStats?.percentageChange).toFixed(2)}%{" "}
                    {stats?.incomeStats?.percentageChange >= 0
                      ? "increase"
                      : "decrease"}
                  </p>
                )}
              </div>
              <div className="rounded-full bg-_bg_dark/20 text-_bg_dark p-3">
                <DollarSign size={24} />
              </div>
            </div>
          </div>

          <div className="rounded-xl bg-_white p-6 shadow-sm transition-all hover:shadow-md">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-_gray">Total Sales</p>
                <p className="mt-2 text-3xl font-bold text-_dark">
                  {stats?.orderStats?.currentCount || 0}
                </p>
                {stats?.orderStats?.percentageChange !== undefined && (
                  <p
                    className={`mt-2 flex items-center text-sm font-medium ${
                      stats?.orderStats?.percentageChange >= 0
                        ? "text-_blue"
                        : "text-_red"
                    }`}
                  >
                    {stats?.orderStats?.percentageChange >= 0 ? (
                      <TrendingUp size={16} className="mr-1" />
                    ) : (
                      <TrendingDown size={16} className="mr-1" />
                    )}
                    {Math.abs(stats?.orderStats?.percentageChange).toFixed(2)}%{" "}
                    {stats?.orderStats?.percentageChange >= 0
                      ? "increase"
                      : "decrease"}
                  </p>
                )}
              </div>
              <div className="rounded-full bg-_bg_lite/20 text-_bg_lite p-3">
                <CreditCard size={24} />
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Charts and Tables */}
      <div className="grid grid-cols-1 gap-6 lg:grid-cols-7">
        <div className="rounded-xl bg-_white p-6 shadow-sm lg:col-span-4">
          <div className="mb-4 flex items-center justify-between">
            <h2 className="text-lg font-semibold text-_dark">
              Monthly Revenue
            </h2>
            <div className="flex items-center space-x-4">
              <div className="flex items-center">
                <div className="mr-2 h-3 w-3 rounded-full bg-_blue"></div>
                <span className="text-xs text-_gray">Revenue</span>
              </div>
              <div className="flex items-center">
                <div className="mr-2 h-3 w-3 rounded-full bg-_dark_lite"></div>
                <span className="text-xs text-_gray">Orders</span>
              </div>
            </div>
          </div>
          <div className="h-80">
            <MonthlyIncomeChart />
          </div>
        </div>

        <div className="rounded-xl bg-_white p-6 shadow-sm lg:col-span-3">
          <div className="mb-4 flex items-center justify-between">
            <h2 className="text-lg font-semibold text-_dark">Latest Orders</h2>
            <Link to={"/admin/orders"}>
              <button className="text-sm font-medium text-_blue hover:opacity-80">
                View All
              </button>
            </Link>
          </div>
          <div className="h-80 overflow-hidden">
            <LatestOrders />
          </div>
        </div>
      </div>

      {/* Optional: Quick Actions Section */}
      <div className="rounded-xl bg-_white p-6 shadow-sm">
        <div className="mb-4 flex items-center justify-between">
          <h2 className="text-lg font-semibold text-_dark">Quick Actions</h2>
        </div>
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4">
          <Link to={"/admin/products/add"}>
            <button
              className="w-full flex items-center rounded-lg bg-_gray_lite text-_dark p-4 transition-all hover:shadow-md disabled:opacity-70"
              disabled={statsLoading}
            >
              <Package className="mr-3" size={20} />
              <span>Add New Product</span>
            </button>
          </Link>

          <Link to={"/admin/customers"}>
            <button
              className="flex w-full items-center rounded-lg bg-_gray_lite text-_dark p-4 transition-all hover:shadow-md disabled:opacity-70"
              disabled={statsLoading}
            >
              <Users className="mr-3" size={20} />
              <span>Manage Customers</span>
            </button>
          </Link>

          <Link to={"/admin/orders"}>
            <button
              className="flex w-full items-center rounded-lg bg-_gray_lite text-_dark p-4 transition-all hover:shadow-md disabled:opacity-70"
              disabled={statsLoading}
            >
              <CreditCard className="mr-3" size={20} />
              <span>Manage Orders</span>
            </button>
          </Link>

          <Link to={"/admin/products"}>
            <button
              className="flex w-full items-center rounded-lg bg-_gray_lite text-_dark p-4 transition-all hover:shadow-md disabled:opacity-70"
              disabled={statsLoading}
            >
              <DollarSign className="mr-3" size={20} />
              <span>Products</span>
            </button>
          </Link>
        </div>
      </div>

      <Footer className="mt-6" />
    </div>
  );
};

export default DashboardPage;
